import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, RotateCcw, Phone, Star, ArrowLeft, UserRound } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { useUsers } from "@/context/AuthContext";
import { dayLabel, initials } from "@/lib/format";
import { MessageBubble } from "@/components/inbox/MessageBubble";
import { Composer } from "@/components/inbox/Composer";
import { DeletionBanner } from "@/components/inbox/DeleteData";

const Header = ({ c, onClosed, onBack, onOpenCrm }) => {
  const qc = useQueryClient();
  const { data: users = [] } = useUsers();
  const patch = async (body, msg) => {
    try {
      await api.patch(`/conversations/${c.id}`, body);
      qc.invalidateQueries({ queryKey: ["conversations"] });
      qc.invalidateQueries({ queryKey: ["conversation", c.id] });
      toast.success(msg);
      if (body.status === "closed") onClosed();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const sendCsat = async () => {
    try {
      await api.post(`/conversations/${c.id}/csat`);
      qc.invalidateQueries({ queryKey: ["conversation", c.id] });
      qc.invalidateQueries({ queryKey: ["messages", c.id] });
      qc.invalidateQueries({ queryKey: ["conversations"] });
      toast.success("Sondaggio inviato al cliente");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="h-16 shrink-0 px-2 md:px-5 flex items-center gap-2 md:gap-3 bg-white border-b border-slate-200">
      <button data-testid="chat-back-button" onClick={onBack} className="md:hidden w-9 h-9 grid place-items-center rounded-lg text-slate-600 hover:bg-slate-100" aria-label="Indietro"><ArrowLeft size={18} /></button>
      <div className="hidden sm:grid w-10 h-10 shrink-0 rounded-full bg-slate-900 text-white place-items-center text-xs font-bold">{initials(c.name || "#")}</div>
      <div className="min-w-0 flex-1">
        <div data-testid="chat-header-name" className="font-bold text-slate-900 truncate text-sm md:text-base">{c.name || c.phone}</div>
        <div className="text-xs text-slate-500 flex items-center gap-1 truncate"><Phone size={11} /><span data-testid="chat-header-phone">{c.phone}</span></div>
      </div>
      <div className="flex items-center gap-1.5 md:gap-2 shrink-0">
        {c.csat_rating && <span data-testid="chat-csat-rating" title="Valutazione cliente" className="hidden sm:flex items-center gap-1 text-xs font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1"><Star size={12} fill="currentColor" />{c.csat_rating}/5</span>}
        {c.csat_pending && <span data-testid="chat-csat-pending" className="hidden lg:inline text-[11px] font-semibold text-slate-500 bg-slate-100 rounded-full px-2.5 py-1">Sondaggio inviato</span>}
        {!c.csat_pending && (
          <Button data-testid="chat-send-csat-button" variant="outline" size="sm" className="h-9 px-2.5 sm:px-3" title="Invia sondaggio di soddisfazione" disabled={!c.window_open} onClick={sendCsat}>
            <Star size={15} className="sm:mr-1.5 text-amber-500" /><span className="hidden lg:inline">Sondaggio</span>
          </Button>
        )}
        <Select value={c.assigned_to || "none"} onValueChange={(v) => patch(v === "none" ? { unassign: true } : { assigned_to: v }, v === "none" ? "Chat rimossa dall'assegnazione" : "Chat assegnata")}>
          <SelectTrigger data-testid="chat-assign-select" className="w-28 sm:w-48 h-9 text-sm"><SelectValue placeholder="Assegna a…" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none" data-testid="assign-option-none">Non assegnata</SelectItem>
            {users.map((u) => (
              <SelectItem key={u.id} value={u.id} data-testid={`assign-option-${u.id}`}>
                <span className="inline-flex items-center gap-2"><i className="w-2 h-2 rounded-full" style={{ background: u.color }} />{u.name}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {c.status === "open" ? (
          <Button data-testid="chat-close-button" variant="outline" size="sm" className="h-9 px-2.5 sm:px-3" title="Chiudi chat" onClick={() => patch({ status: "closed" }, "Chat chiusa e archiviata")}>
            <Archive size={15} className="sm:mr-1.5" /><span className="hidden sm:inline">Chiudi chat</span>
          </Button>
        ) : (
          <Button data-testid="chat-reopen-button" size="sm" className="h-9 px-2.5 sm:px-3 bg-[#25D366] hover:bg-[#22C55E] text-slate-900" title="Riapri" onClick={() => patch({ status: "open" }, "Chat riaperta")}>
            <RotateCcw size={15} className="sm:mr-1.5" /><span className="hidden sm:inline">Riapri</span>
          </Button>
        )}
        <Button data-testid="chat-open-crm-button" variant="outline" size="sm" className="xl:hidden h-9 px-2.5" title="Scheda cliente" onClick={onOpenCrm}>
          <UserRound size={15} />
        </Button>
      </div>
    </div>
  );
};

export const ChatWindow = ({ conversation: c, onClosed, onBack, onOpenCrm }) => {
  const endRef = useRef(null);
  const { data: messages = [] } = useQuery({
    queryKey: ["messages", c.id],
    queryFn: () => api.get(`/conversations/${c.id}/messages`).then((r) => r.data),
    refetchInterval: 3000,
  });
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);

  let lastDay = null;
  return (
    <section className="flex-1 min-w-0 flex flex-col" data-testid="chat-window">
      <Header c={c} onClosed={onClosed} onBack={onBack} onOpenCrm={onOpenCrm} />
      <DeletionBanner conversation={c} onDeleted={onClosed} />
      <div className="flex-1 overflow-y-auto thin-scroll chat-wallpaper px-3 md:px-8 py-4 md:py-6 space-y-2" data-testid="chat-messages">
        {messages.map((m) => {
          const day = dayLabel(m.created_at);
          const showDay = day !== lastDay;
          lastDay = day;
          return (
            <div key={m.id}>
              {showDay && <div className="flex justify-center my-3"><span className="text-[11px] font-semibold bg-white/90 text-slate-500 px-3 py-1 rounded-md shadow-sm capitalize">{day}</span></div>}
              <MessageBubble m={m} />
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <Composer conversation={c} />
    </section>
  );
};
